import { transformDocumentNumber } from '@/components/custom/erp';
import { isValidUUID } from '@/lib/uuid-validator';
import { MA_CHUNG_TU } from '@/constants';
import { FormFieldState } from '../hooks';

/**
 * Transform service detail rows for API submission
 * @param detailRows - Array of service detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (detailRows: BanHangRecord[]): BanHangRecord[] => {
  return detailRows.map((row: BanHangRecord, index: number) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) ? { uuid: row.uuid } : {}),
    tk_no: row.tk_no_data?.uuid || null,
    dvt: row.dvt_data?.uuid || null,
    so_luong: Number(row.so_luong) || 0,
    gia_nt2: Number(row.gia_nt2) || 0,
    tien_nt2: Number(row.tien_nt2 || 0),
    dien_giai: row.dien_giai || null,
    ma_thue: row.ma_thue_data?.uuid || null,
    thue_suat: Number(row.ma_thue_data?.thue_suat || 0),
    tk_thue_no: row.tk_thue_no_data?.uuid || null,
    thue_nt: Number(row.thue_nt || 0),
    gia2: Number(row.gia_ban || 0),
    tien2: Number(row.thanh_tien || 0),

    // Additional fields
    ma_bp: row.ma_bp_data?.uuid || null,
    ma_vv: row.ma_vv_data?.uuid || null,
    ma_hd: row.ma_hd_data?.uuid || null,
    ma_dtt: row.ma_dtt_data?.uuid || null,
    ma_ku: row.ma_ku_data?.uuid || null,
    ma_phi: row.ma_phi_data?.uuid || null,
    ma_sp: row.ma_sp_data?.uuid || null,
    ma_lsx: row.lenh_sx || null,
    ma_cp0: row.ma_cp0_data?.uuid || null,
    id_hd: row.id_hd?.uuid || null,
    line_hd: row.line_hd?.uuid || null
  }));
};

/**
 * Transform all service invoice form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of service detail row data
 * @param totalAmount - Total amount calculated
 * @param totalDiscount - Total discount calculated
 * @param totalTax - Total tax calculated
 * @param entity - Current entity information
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: BanHangPayload,
  entity: BanHangRecord,
  state: FormFieldState,
  detailRows: BanHangRecord[],
  totalAmount: number = 0,
  totalDiscount: number = 0,
  totalTax: number = 0
) => {
  // Transform detail and tax rows
  const chi_tiet = transformDetailRows(detailRows);

  // Calculate final totals
  const finalAmount = totalAmount - totalDiscount + totalTax;

  // Build the final form data object
  return {
    // Basic entity and document info
    unit_id: entity?.uuid || null,
    ...data,
    ma_kh: state.khachHang?.uuid || null,
    tk: state.taiKhoan?.uuid || state.khachHang?.account_data?.uuid || null,
    ma_nvbh: state.nhanVien?.uuid || state.khachHang?.sales_rep_data?.uuid || null,
    ma_tthddt: data.ma_tthddt || null,

    // Customer information from state and form
    ma_so_thue: state.khachHang?.tax_code || data.ma_so_thue || null,
    ten_kh_thue: state.khachHang?.customer_name || data.ten_kh_thue || null,
    dia_chi: state.khachHang?.address || data.dia_chi || null,
    ong_ba: data.ong_ba || state.khachHang?.contact_person || null,
    e_mail: data.e_mail || state.khachHang?.email || null,
    dien_giai: state.dienGiai || null,
    ma_mau_ct: state.mauHoaDon?.uuid || null,
    ma_tc_thue: state.tinhChatThue?.uuid || null,
    ma_tt: state.hanThanhToan?.uuid || null,

    // Document dates and references
    ngay_ct: data.ngay_ct || null,
    ngay_lct: data.ngay_lct || null,

    // Currency and exchange rate
    ma_nt: data.ma_nt || null,
    ty_gia: state.ty_gia || 1,
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTuHienTai),

    // Status and other fields
    status: data.status || null,
    ly_do_huy: data.ly_do_huy || null,
    ly_do: data.ly_do || null,
    ghi_chu: data.ghi_chu || null,

    // E-invoice information
    so_ct_hddt: data.so_ct_hddt || null,
    so_ct2_hddt: data.so_ct2_hddt || null,
    ma_mau_ct_hddt: data.ma_mau_ct_hddt || null,

    // Payment and processing flags
    pt_tao_yn: state.pt_tao_yn,
    transfer_yn: state.transfer_yn,
    ma_httt: data.ma_httt || 'TMB',
    ma_pttt: data.ma_pttt || null,

    // Additional references
    ma_ngv: '1',
    ma_kh9: state.coQuanThue?.uuid || null,

    // Financial totals
    t_tien_nt2: Math.round(totalAmount * 100) / 100,
    t_tien2: Math.round(totalAmount * (state.ty_gia || 1) * 100) / 100,
    t_thue_nt: Math.round(totalTax * 100) / 100,
    t_thue: Math.round(totalTax * (state.ty_gia || 1) * 100) / 100,
    t_tt_nt: Math.round(finalAmount * 100) / 100,
    t_tt: Math.round(finalAmount * (state.ty_gia || 1) * 100) / 100,

    // Detail arrays
    chi_tiet
  };
};
